import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../AuthProvider';
import dexie from '../offlineDB';
import { useUserPrefs } from '../UserPrefsProvider';
import WhatsNewBannerComponent from './WhatsNewBanner';
import ProgressiveNudgeComponent from './ProgressiveNudge';

const OnboardingContext = createContext(null);

// Safe fallback so pages don't crash if used outside provider
const FALLBACK = {
  showWizard: false,
  showWhatsNew: false,
  showTheSessionPrompt: false,
  isSimpleMode: false,
  complexityLevel: 'full',
  nudgeType: null,
  nudgeTuneCount: 0,
  nudgeSetCount: 0,
  WhatsNewBanner: null,
  NudgeBanner: null,
  unlockFullMode: () => {},
  restartWizard: () => {},
  toggleComplexity: () => {},
  dismissNudge: () => {},
  dismissWhatsNew: () => {},
  dismissTheSessionPrompt: () => {},
  refreshCounts: () => {},
};

export function useOnboarding() {
  return useContext(OnboardingContext) || FALLBACK;
}

export function OnboardingProvider({ children }) {
  const { user } = useAuth();
  const { prefs, savePrefs, patchPrefs } = useUserPrefs();
  const location = useLocation();
  const navigate = useNavigate();

  const [tunesCount, setTunesCount] = useState(0);
  const [setsCount, setSetsCount] = useState(0);
  const [sessionsCount, setSessionsCount] = useState(0);

  const loadCounts = useCallback(async () => {
    try {
      if (navigator.onLine && user) {
        const [tunesSnap, setsSnap, sessSnap] = await Promise.all([
          getDocs(collection(db, 'users', user.uid, 'tunes')),
          getDocs(collection(db, 'users', user.uid, 'sets')),
          getDocs(collection(db, 'users', user.uid, 'sessions')),
        ]);
        setTunesCount(tunesSnap.docs.filter(d => !d.data().isSample).length);
        setSetsCount(setsSnap.size);
        setSessionsCount(sessSnap.size);
      } else {
        const [tc, sc, sess] = await Promise.all([
          dexie.tunes.filter(t => !t.isSample).count(),
          dexie.sets.count(),
          dexie.sessions.count(),
        ]);
        setTunesCount(tc);
        setSetsCount(sc);
        setSessionsCount(sess);
      }
    } catch (_) {}
  }, [user]);

  // Refresh counts on every route change (catches adds/deletes from other pages)
  useEffect(() => { loadCounts(); }, [location.pathname, loadCounts]);

  // ─── Derived state ────────────────────────────────────────────────────
  const complexityLevel = prefs?.complexityLevel || 'full';
  const isSimpleMode = complexityLevel === 'simple';
  const appMode = prefs?.appMode || 'tunes';

  // New user: never completed old tour AND never completed new wizard
  const showWizard = Boolean(prefs && !prefs.tutorialDismissed && !prefs.onboardingComplete);

  // Existing user on first post-update visit
  const showWhatsNew = Boolean(prefs && prefs.tutorialDismissed && !prefs.onboardingComplete);

  // Post-wizard Irish prompt — strictly Irish only, never for notes-only users
  const showTheSessionPrompt = Boolean(
    prefs?.onboardingComplete &&
    (prefs?.customGenres?.tunes || []).includes('Irish') &&
    !prefs?.dismissedNudges?.theSessionImport
  );

  // Progressive nudge — suppress only while the wizard is actively open
  const nudgeType = useMemo(() => {
    if (showWizard) return null;
    if (appMode === 'notes') return null;
    if (tunesCount === 0 && location.pathname !== '/add') return 'firstTune';
    if (tunesCount >= 3 && setsCount === 0 && !prefs?.dismissedNudges?.makeSet) return 'makeSet';
    if (setsCount > 0 && sessionsCount === 0 && !isSimpleMode && !prefs?.dismissedNudges?.startSession) return 'startSession';
    if (tunesCount >= 10 && !prefs?.dismissedNudges?.settings) return 'settings';
    if (tunesCount >= 15 && !prefs?.dismissedNudges?.social) return 'social';
    return null;
  }, [prefs, tunesCount, setsCount, sessionsCount, isSimpleMode, appMode, location.pathname]);

  // ─── Actions ──────────────────────────────────────────────────────────
  const unlockFullMode = useCallback(async () => {
    if (!prefs) return;
    await patchPrefs({ complexityLevel: 'full' });
  }, [prefs, patchPrefs]);

  const restartWizard = useCallback(async () => {
    if (!prefs) return;
    await patchPrefs({ tutorialDismissed: false, onboardingComplete: false });
    navigate('/search');
  }, [prefs, patchPrefs, navigate]);

  const toggleComplexity = useCallback(async () => {
    if (!prefs) return;
    await patchPrefs({ complexityLevel: isSimpleMode ? 'full' : 'simple' });
  }, [prefs, patchPrefs, isSimpleMode]);

  const dismissNudge = useCallback(async (key) => {
    if (!prefs) return;
    await patchPrefs({ dismissedNudges: { ...(prefs.dismissedNudges || {}), [key]: true } });
  }, [prefs, patchPrefs]);

  const dismissWhatsNew = useCallback(async (action) => {
    if (!prefs) return;
    if (action === 'simple') {
      await patchPrefs({ complexityLevel: 'simple', onboardingComplete: true });
    } else if (action === 'rerun') {
      await patchPrefs({ tutorialDismissed: false, onboardingComplete: false });
    } else {
      await patchPrefs({ onboardingComplete: true });
    }
  }, [prefs, patchPrefs]);

  const dismissTheSessionPrompt = useCallback(async () => {
    if (!prefs) return;
    await patchPrefs({ dismissedNudges: { ...(prefs.dismissedNudges || {}), theSessionImport: true } });
  }, [prefs, patchPrefs]);

  // ─── Return component references (null = don't render) ───────────────
  // Pages receive these and render with `{WhatsNewBanner && <WhatsNewBanner />}`
  const WhatsNewBanner = showWhatsNew ? WhatsNewBannerComponent : null;
  const NudgeBanner = nudgeType ? ProgressiveNudgeComponent : null;

  const value = {
    showWizard,
    showWhatsNew,
    showTheSessionPrompt,
    isSimpleMode,
    complexityLevel,
    nudgeType,
    nudgeTuneCount: tunesCount,
    nudgeSetCount: setsCount,
    WhatsNewBanner,
    NudgeBanner,
    unlockFullMode,
    restartWizard,
    toggleComplexity,
    dismissNudge,
    dismissWhatsNew,
    dismissTheSessionPrompt,
    refreshCounts: loadCounts,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}
